"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function LanguageToggle() {
  const [lang, setLang] = useState<"EN" | "ES">("EN");
  return <div role="button" tabIndex={0} onClick={() => setLang(lang === "EN" ? "ES" : "EN")} onKeyDown={e => {
    if (e.key === "Enter" || e.key === " ") {
      setLang(lang === "EN" ? "ES" : "EN");
    }
  }} className="relative flex h-8 w-20 cursor-pointer items-center rounded-full bg-secondary/20 p-1 ring-1 ring-primary/20 transition-all hover:ring-primary/50 focus:outline-none focus:ring-2 focus:ring-primary" aria-label="Toggle Language">
      <div className={`absolute h-6 w-9 rounded-full bg-primary shadow-sm transition-all duration-300 ${lang === "EN" ? "translate-x-0" : "translate-x-9"}`} />
      <span className={`relative z-10 flex w-1/2 justify-center text-[10px] font-bold tracking-wider transition-colors ${lang === "EN" ? "text-primary-foreground" : "text-muted-foreground"}`}>
        EN
      </span>
      <span className={`relative z-10 flex w-1/2 justify-center text-[10px] font-bold tracking-wider transition-colors ${lang === "ES" ? "text-primary-foreground" : "text-muted-foreground"}`}>
        ES
      </span>
    </div>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 animate-pulse rounded bg-muted/20" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent text-sm font-medium text-foreground/90 hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary data-[state=open]:bg-transparent data-[state=open]:text-primary">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="min-w-[200px] rounded-md border border-border/50 bg-background/95 p-2 shadow-xl backdrop-blur-md">
                  <ul className="grid gap-1">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none rounded-sm px-3 py-2 text-sm leading-none text-muted-foreground no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative text-sm font-medium text-foreground/90 transition-colors hover:text-primary">
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-6 w-32 animate-pulse rounded bg-muted/20" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-1 p-4">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border/40 py-3 last:border-0">
          {item.children && item.children.length > 0 ? <div className="flex flex-col gap-2">
              <span className="font-serif text-lg font-medium text-primary">{item.label}</span>
              <div className="ml-4 flex flex-col gap-2 border-l border-border/40 pl-4">
                {item.children.map((child, j) => <Link key={child.id} data-index={j} to={child.href || "#"} className="text-base text-muted-foreground hover:text-foreground" onClick={onClose}>
                    {child.label}
                  </Link>)}
              </div>
            </div> : <Link to={item.href || "#"} className="block font-serif text-lg font-medium text-foreground hover:text-primary" onClick={onClose}>
              {item.label}
            </Link>}
        </div>)}
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="main-navigation" className={`sticky top-0 z-50 w-full border-b transition-all duration-500 dark ${isScrolled ? "border-border/40 bg-background/80 shadow-lg backdrop-blur-xl" : "border-transparent bg-background"}`}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        {}
        <Link to="/" className="flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-primary">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/cohcc_official_logo.png" alt="COHCC" className="h-10 w-auto object-contain md:h-12" />
        </Link>

        {}
        <div className="hidden lg:block">
          <MenuProvider menu_id="71" className="flex items-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          <div className="hidden sm:block">
            <LanguageToggle />
          </div>

          <Button asChild className="relative hidden overflow-hidden border-primary/20 bg-primary text-primary-foreground shadow-[0_0_15px_rgba(var(--primary),0.3)] transition-all hover:bg-primary/90 hover:shadow-[0_0_20px_rgba(var(--primary),0.5)] md:inline-flex">
            <Link to="/JoinUsPage">
              <span className="relative z-10">Join Now</span>
              <div className="absolute inset-0 animate-[pulse_3s_ease-in-out_infinite] bg-white/10" />
            </Link>
          </Button>

          {}
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-accent hover:text-accent-foreground lg:hidden" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="absolute left-0 top-full h-[calc(100vh-5rem)] w-full overflow-y-auto bg-background/95 px-4 pb-10 pt-4 backdrop-blur-xl lg:hidden">
          <div className="mb-6 flex items-center justify-between border-b border-border/40 pb-4 sm:hidden">
            <span className="text-sm font-medium text-muted-foreground">Language</span>
            <LanguageToggle />
          </div>
          
          <MenuProvider menu_id="71">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>

          <div className="mt-8 flex flex-col gap-4">
            <Button asChild className="w-full bg-primary text-primary-foreground shadow-lg">
              <Link to="/JoinUsPage" onClick={() => setIsMobileMenuOpen(false)}>
                Join Now
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full border-primary/30 text-foreground hover:bg-primary/10">
              <Link to="/ContactPage" onClick={() => setIsMobileMenuOpen(false)}>
                Contact Us
              </Link>
            </Button>
          </div>
        </div>}
    </header>;
}